import React, { useMemo } from "react";
import PropTypes from "prop-types";
import NextImage from "next/image";
import Link from "next/link";
import { DateTime } from "luxon";
import { unescape } from "lodash";
import ClockIcon from "components/icons/clock";
import LocationIcon from "components/icons/location";
import styled, { withTheme, css } from "styled-components";
import generateRandomImagePlaceholderPath from "lib/generate-random-image-placeholder";
import ServiceTypePills from "components/ServiceTypePills";
import Availability from "components/BusinessAvailabilityDisplay";
import YotpoStarRatingsWidget from "components/YotPoReviewsWidget/StarRating";


const Container = styled.li`
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  margin: 0;

  :hover {
    background-color: ${({ theme }) => theme.themeColors.lightGreen1};
  }

  ${({ theme }) => theme.mq.lg`
    margin-bottom: 0;

    ${({ active }) =>
      active &&
      css`
        background-color: ${theme.themeColors.lightGreen1};
      `}
  `}
`;

const Wrapper = styled.div`
  margin: 0 auto;
  width: 100%;
  padding: 1.875rem 0;
  border-top: ${({first, theme}) => first ? 'none' : `solid 0.0625rem ${theme.themeColors.lightGrey}`};

  ${({ theme }) => theme.mq.lg`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `}
`;

const Content = styled.a`
  display: grid;
  cursor: pointer;
  column-gap: 1rem;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto auto 1.3rem 2.5rem;
  row-gap: 0.5rem;
  grid-template-areas:
    "image "
    "service-types"
    "title "
    "rating "
    "address "
    "availability ";

  ${({ theme }) => theme.mq.sm`
    flex-direction: row;
    grid-template-columns: 12rem 1fr;
    grid-template-rows: 1.75rem auto 1.3rem 2.5rem;
    grid-template-areas:
      "image service-types "
      "image title "
      "image rating"
      "image address "
      " . availability";
    margin-bottom: 0;
  `}

  ${({ theme }) => theme.mq.lg`
    grid-template-rows: auto auto auto auto auto;
    align-content: flex-start;
    grid-auto-rows: fit-content(1.5rem);
    grid-template-columns: 12rem 1fr;
    grid-template-areas:
      "image service-types "
      "image title "
      "image rating "
      "image address ";
    row-gap: 0;
  `}
`;

const ImageContainer = styled.div`
  grid-area: image;
  position: relative;
  border-radius: 0.75rem;
  overflow: hidden;
  height: 17rem;

  ${({ theme }) => theme.mq.sm`
    height: 9rem;
  `};
`;

const Image = styled(NextImage)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
`;

const ServiceTypes = styled.div`
  grid-area: service-types;
  margin-top: 0.5rem;

  ${({ theme }) => theme.mq.sm`
    margin-top: 0;
  `};
`;

const Title = styled.h3`
  grid-area: title;
  padding: 0;
  margin: 0;
  font-size: 1.5rem;
  display: inline-block;
  min-width: 16.75rem;
  overflow-wrap: break-word;
  line-height: 1;
`;

const StyledStarRating = styled(YotpoStarRatingsWidget)`
  grid-area: rating;
`;

const Address = styled.p`
  grid-area: address;
  padding: 0;
  margin: 0;
  font-weight: 400;
  display: inline-block;
  min-width: 16.75rem;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.themeColors.darkGreen4};
`;

const Hours = styled.p`
  grid-area: hours;
  padding: 0;
  margin: 0;
  font-weight: 400;
  display: flex;
  flex-direction: row;
  align-items: start-end;
  color: ${({ theme }) => theme.themeColors.darkGreen4};

  svg {
    margin-right: 0.25rem;
  }
`;

const MobileAvailabilityWrapper = styled.div`
  grid-area: availability;

  ${({ theme }) => theme.mq.lg`
  display: none;
  `};
`;

const DesktopAvailabilityWrapper = styled.div`
  display: none;

  ${({ theme }) => theme.mq.lg`
    display: inherit;
  `};
`;

function formatTime(t) {
  return DateTime.fromISO(t).toFormat("h:mm a");
}

function BusinessResultItem({
  id,
  visible,
  friendlyName,
  name,
  serviceTypes,
  imageUrl,
  activeBusinessId,
  formattedAddress,
  startTime,
  endTime,
  biz,
  dateArr,
  groupedTimeslots,
  list,
  mobileDateArr,
  onHover,
  setShowChangeRoleModal,
  isCustomerUser,
  isVendorUser,
  isClaimed,
  isAcceptingBooking,
  isMobile,
  showCustomerModalSetPath,
  urlLocality,
  urlRegion,
  updateSelectedDay,
  first,
}) {
  const hours = useMemo(() => {
    if (!startTime || !endTime) {
      return null;
    }

    return (
      <Hours>
        <ClockIcon /> {formatTime(startTime)} - {formatTime(endTime)}
      </Hours>
    );
  }, [startTime, endTime]);
  return (
    <Container
      onMouseEnter={() => onHover(id)}
      active={id === activeBusinessId}
    >
      <Wrapper first={first}>
        <Link passHref
          href={`/business/${urlRegion}/${urlLocality}/${friendlyName}${
            visible ? "?visible=true" : ""
          }`}
        >
          <Content>
            <ImageContainer>
              <Image
                alt={unescape(name)}
                objectFit="cover"
                src={
                  imageUrl || generateRandomImagePlaceholderPath(id, serviceTypes)
                }
                layout="fill"
              />
            </ImageContainer>
            <ServiceTypes>
              <ServiceTypePills serviceTypes={serviceTypes} parent='BusinessResultItem' />
            </ServiceTypes>
            <Title>{unescape(name)}</Title>
            <StyledStarRating
              averageYotpoScore={biz?.attributes?.averageYotpoScore}
              totalYotpoReviews={biz?.attributes?.totalYotpoReviews}
            />
            {formattedAddress && (
              <Address>
                <LocationIcon /> {formattedAddress}
              </Address>
            )}
            {hours}
            <MobileAvailabilityWrapper isMobile>
              <Availability
                biz={biz}
                dateArr={dateArr}
                groupedTimeslots={groupedTimeslots}
                isCustomerUser={isCustomerUser}
                isVendorUser={isVendorUser}
                mobileDateArr={mobileDateArr}
                list={list}
                setShowChangeRoleModal={setShowChangeRoleModal}
                showCustomerModalSetPath={showCustomerModalSetPath}
                isActive={id === activeBusinessId}
                isClaimed={isClaimed}
                isAcceptingBooking={isAcceptingBooking}
                isMobile
                updateSelectedDay={updateSelectedDay}
              />
            </MobileAvailabilityWrapper>
          </Content>
        </Link>
        <DesktopAvailabilityWrapper>
          <Availability
            biz={biz}
            dateArr={dateArr}
            groupedTimeslots={groupedTimeslots}
            isCustomerUser={isCustomerUser}
            isVendorUser={isVendorUser}
            mobileDateArr={mobileDateArr}
            list={list}
            setShowChangeRoleModal={setShowChangeRoleModal}
            showCustomerModalSetPath={showCustomerModalSetPath}
            isActive={id === activeBusinessId}
            isClaimed={isClaimed}
            isMobile={isMobile}
            isAcceptingBooking={isAcceptingBooking}
            updateSelectedDay={updateSelectedDay}
          />
        </DesktopAvailabilityWrapper>
      </Wrapper>

    </Container>
  );
}

export default withTheme(BusinessResultItem);

BusinessResultItem.propTypes = {
  activeBusinessId: PropTypes.string,
  isAcceptingBooking: PropTypes.bool,
  id: PropTypes.string.isRequired,
  friendlyName: PropTypes.string.isRequired,
  name: PropTypes.string,
  serviceTypes: PropTypes.array.isRequired,
  formattedAddress: PropTypes.string,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  imageUrl: PropTypes.string,
  isCustomerUser: PropTypes.bool,
  isVendorUser: PropTypes.bool,
  isClaimed: PropTypes.bool,
  isMobile: PropTypes.bool,
  visible: PropTypes.bool,
  onHover: PropTypes.func,
  updateSelectedDay: PropTypes.func,
  list: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ])
  ),
  dateArr: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ])
  ),
  mobileDateArr: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ])
  ),
  groupedTimeslots: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({}),
      ])
    ),
  ]),
  biz: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    attributes: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        businessName: PropTypes.string,
      }),
    ]),
  }),
  theme: PropTypes.shape({
    breakpoints: PropTypes.shape({
      lg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  setShowChangeRoleModal: PropTypes.func,
  showCustomerModalSetPath: PropTypes.func,
  urlLocality: PropTypes.string.isRequired,
  urlRegion: PropTypes.string.isRequired,
  first: PropTypes.bool,
};

BusinessResultItem.defaultProps = {
  showCustomerModalSetPath: () => {},
  imageUrl: undefined,
  name: "Untitled business",
  formattedAddress: undefined,
  isAcceptingBooking: false,
  activeBusinessId: undefined,
  startTime: undefined,
  endTime: undefined,
  isCustomerUser: false,
  isClaimed: false,
  isMobile: false,
  visible: false,
  isVendorUser: false,
  onHover: () => {},
  updateSelectedDay: () => {},
  list: [],
  dateArr: [],
  mobileDateArr: [],
  groupedTimeslots: [],
  biz: {
    id: undefined,
    attributes: {
      businessName: undefined,
    },
  },
  theme: {
    breakpoints: {
      lg: 992,
    },
  },
  setShowChangeRoleModal: () => {},
  first: false
};
